import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Sparkles, X, Minus, Send, Loader2, Trash2, ArrowRight } from '@/components/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_MESSAGES = 'haccp-assistant-messages-v2';
const STORAGE_STATE = 'haccp-assistant-state-v2';
const MAX_LOCAL_MESSAGES = 100;

const readSession = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const HaccpChat = ({ openRequest = 0 }) => {
  const navigate = useNavigate();
  const { user, hasFeature, plan } = useAuth();
  const canUseProData = hasFeature('production.manage') && hasFeature('recipes.manage') && hasFeature('traceability.advanced');
  const starters = useMemo(() => canUseProData ? [
    'Cosa scade questa settimana?',
    'Che lotti ho disponibili?',
    'Che produzioni ho registrato oggi?',
    'Dove ho usato un lotto?',
  ] : [
    'Cosa scade questa settimana?',
    'Che lotti ho disponibili?',
    'Cosa ho scaricato oggi?',
    'Quali movimenti ha avuto un lotto?',
  ], [canUseProData]);
  const greeting = useMemo(() => ({
    role: 'assistant',
    content: canUseProData
      ? 'Chiedimi informazioni su magazzino, ricette, produzioni e tracciabilità.'
      : 'Chiedimi informazioni su magazzino, lotti, scadenze, carichi e scarichi.',
    local: true,
  }), [canUseProData]);
  const storageScope = String(user?.company_id || 'no-company') + ':' + String(user?.user_id || user?.email || 'anonymous') + ':' + String(plan || 'no-plan');
  const messagesKey = `${STORAGE_MESSAGES}:${storageScope}`;
  const stateKey = `${STORAGE_STATE}:${storageScope}`;
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(() => readSession(messagesKey, [greeting]));
  const [conversationState, setConversationState] = useState(() => readSession(stateKey, {}));
  const endRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    setMessages(readSession(messagesKey, [greeting]));
    setConversationState(readSession(stateKey, {}));
    setInput('');
    setOpen(false);
    setMinimized(false);
  }, [messagesKey, stateKey, greeting]);

  useEffect(() => {
    if (openRequest > 0) {
      setOpen(true);
      setMinimized(false);
    }
  }, [openRequest]);

  useEffect(() => {
    try {
      sessionStorage.setItem(messagesKey, JSON.stringify(messages.slice(-MAX_LOCAL_MESSAGES)));
      sessionStorage.setItem(stateKey, JSON.stringify(conversationState || {}));
    } catch {
      // Session storage is optional; the chat still works if disabled.
    }
  }, [messages, conversationState, messagesKey, stateKey]);

  useEffect(() => {
    if (open && !minimized) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, open, minimized]);

  useEffect(() => {
    if (open && !minimized) {
      const timer = setTimeout(() => textareaRef.current?.focus(), 120);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, minimized]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const historyForApi = useMemo(() => {
    return messages
      .filter((message) => !message.local && (message.role === 'user' || message.role === 'assistant'))
      .slice(-6)
      .map(({ role, content }) => ({ role, content: String(content || '').slice(0, 1200) }));
  }, [messages]);

  const clearChat = () => {
    setMessages([greeting]);
    setConversationState({});
    setInput('');
    try {
      sessionStorage.removeItem(messagesKey);
      sessionStorage.removeItem(stateKey);
    } catch {
      // Ignore storage errors.
    }
  };

  const sendMessage = async (textOverride) => {
    const text = String(textOverride ?? input).trim();
    if (!text || loading) return;

    const userMessage = { role: 'user', content: text };
    const priorHistory = historyForApi;
    setMessages((prev) => [...prev, userMessage].slice(-MAX_LOCAL_MESSAGES));
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/assistant/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: priorHistory,
          state: conversationState || {},
        }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const detail = data?.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Assistente temporaneamente non disponibile.');
      }

      setConversationState(data?.state || conversationState || {});
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data?.answer || 'Non ho trovato una risposta disponibile.',
          actions: Array.isArray(data?.actions) ? data.actions : [],
        },
      ].slice(-MAX_LOCAL_MESSAGES));
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: error?.message || 'Assistente temporaneamente non disponibile. Riprova tra poco.',
          error: true,
        },
      ].slice(-MAX_LOCAL_MESSAGES));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleAction = (action) => {
    if (action?.type === 'navigate' && action?.path) {
      navigate(action.path);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => { setOpen(true); setMinimized(false); }}
          className="klaro-chat-floating-launcher fixed right-4 bottom-4 sm:right-5 sm:bottom-5 z-[90] h-12 w-12 rounded-full bg-primary text-white shadow-lg shadow-primary/20 border border-white/30 items-center justify-center hover:-translate-y-0.5 hover:shadow-xl transition-[transform,box-shadow,background-color] duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2"
          aria-label="Apri Chat HACCP AI"
          title="Chat HACCP AI"
          data-testid="haccp-chat-launcher"
        >
          <MessageCircle className="w-5 h-5" strokeWidth={1.8} />
          <Sparkles className="absolute w-3.5 h-3.5 -top-0.5 -right-0.5 text-secondary fill-secondary/20" strokeWidth={1.8} />
        </button>
      )}

      {open && (
        <section
          className={`fixed z-[95] bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col transition-[height,transform,opacity] duration-200
            inset-3 sm:inset-auto sm:right-5 sm:bottom-5 sm:w-[420px] sm:rounded-2xl
            ${minimized ? 'h-[58px] sm:h-[58px]' : 'h-[calc(100dvh-1.5rem)] sm:h-[min(620px,calc(100dvh-2.5rem))] rounded-2xl'}`}
          aria-label="Chat HACCP AI"
          data-testid="haccp-chat-popup"
        >
          <header className="h-[58px] shrink-0 px-3.5 border-b border-slate-200 flex items-center justify-between bg-white">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Sparkles className="w-[17px] h-[17px]" strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-900 leading-tight">Chat HACCP AI</div>
                <div className="text-[11px] text-slate-500 truncate">Interroga i dati della tua attività</div>
              </div>
            </div>

            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={clearChat}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label="Nuova conversazione"
                title="Nuova conversazione"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setMinimized((value) => !value)}
                className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label={minimized ? 'Espandi chat' : 'Riduci chat'}
                title={minimized ? 'Espandi chat' : 'Riduci chat'}
              >
                <Minus className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label="Chiudi chat"
                title="Chiudi chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </header>

          {!minimized && (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto px-3.5 py-3 bg-slate-50/60">
                {messages.length <= 1 && (
                  <div className="mb-3 grid grid-cols-1 gap-1.5">
                    {starters.map((starter) => (
                      <button
                        key={starter}
                        type="button"
                        onClick={() => sendMessage(starter)}
                        className="text-left px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 hover:border-primary/30 hover:text-primary hover:bg-primary/[0.03] transition-colors"
                      >
                        {starter}
                      </button>
                    ))}
                  </div>
                )}

                <div className="space-y-2.5">
                  {messages.map((message, index) => (
                    <div key={`${message.role}-${index}`} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] ${message.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1.5`}>
                        <div
                          className={`rounded-2xl px-3 py-2 text-[13px] leading-[1.45] whitespace-pre-wrap break-words shadow-sm
                            ${message.role === 'user'
                              ? 'bg-primary text-white rounded-br-md'
                              : message.error
                                ? 'bg-red-50 text-red-700 border border-red-100 rounded-bl-md'
                                : 'bg-white text-slate-700 border border-slate-200 rounded-bl-md'}`}
                        >
                          {message.content}
                        </div>

                        {Array.isArray(message.actions) && message.actions.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {message.actions.map((action, actionIndex) => (
                              <button
                                key={`${action.label}-${actionIndex}`}
                                type="button"
                                onClick={() => handleAction(action)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-primary/20 bg-white text-[11px] font-medium text-primary hover:bg-primary/5 transition-colors"
                              >
                                {action.label}
                                <ArrowRight className="w-3 h-3" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="inline-flex items-center gap-2 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 shadow-sm">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Sto verificando i dati…
                      </div>
                    </div>
                  )}
                  <div ref={endRef} />
                </div>
              </div>

              <footer className="shrink-0 p-3 border-t border-slate-200 bg-white">
                <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/10 transition-[border-color,box-shadow]">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(event) => setInput(event.target.value.slice(0, 1000))}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    placeholder="Scrivi una domanda…"
                    className="flex-1 resize-none bg-transparent border-0 outline-none text-[13px] leading-5 text-slate-800 placeholder:text-slate-400 max-h-24 min-h-5 py-0.5"
                    aria-label="Scrivi una domanda"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => sendMessage()}
                    disabled={loading || !input.trim()}
                    className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg bg-primary text-white disabled:opacity-35 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                    aria-label="Invia domanda"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
                <div className="mt-1.5 px-1 text-[10px] text-slate-400">
                  Sola lettura · verifica sempre le decisioni HACCP operative
                </div>
              </footer>
            </>
          )}
        </section>
      )}
    </>
  );
};

export default HaccpChat;
